// React dependencies
import React, { useEffect, useState, useRef } from 'react';

// External Dependencies
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { TextField } from '@material-ui/core';

// Internal Dependencies
import PhotosComponent from '../photos/photos';
import apiService from '../../services.js/apiService';
import MText from '../common/MText';
import MButton from '../common/MButton';
import Colours from '../../styles/colours';
import MapModal from './MapModal';
import PoiMap from '../common/poi/poiMap';
import { IfMapCoordInvalidThrowError, IsEmpty } from '../../common/checks';
import { Trim } from '../../common/util';
import PageSuggestionField from '../common/suggestions/PageSuggestionField';
import { PageType, Status } from '../../common/constants';
import Images from '../basics/Images';
import convert from '../../common/convert';

const defaultPhotoInfo = {
  id:             null,
  lat:            null,
  lng:            null,
  display:        '',
  original:       '',
  isEditable:     true,
  isDeletable:    true,
  shopperId:      null,
  businessUserId: null,
  commentId:      null,
  page:           { id: null, name: '' },
  info:           { contributor: '', takenOn: null, text: '' },
  poiId:          null,
};

const formatPhoto = (p) => {
  if (IsEmpty(p)) {
    return defaultPhotoInfo;
  }

  const isStaffRestricted = IsEmpty(p.businessUserId) && IsEmpty(p.shopperId) && IsEmpty(p.commentId);

  return {
    id:             IsEmpty(p.id) ? defaultPhotoInfo.id : p.id,
    lat:            IsEmpty(p.lat) ? defaultPhotoInfo.lat : p.lat,
    lng:            IsEmpty(p.lng) ? defaultPhotoInfo.lng : p.lng,
    display:        IsEmpty(p.cropUrl) || p.cropUrl === p.url ? convert.imageResizeUrl(p.url, 300, 300) : p.cropUrl,
    original:       IsEmpty(p.url) ? defaultPhotoInfo.original : p.url,
    page:           IsEmpty(p.pageId) ? defaultPhotoInfo.page : { id: p.pageId, name: p.pageName },
    shopperId:      IsEmpty(p.shopperId) ? defaultPhotoInfo.shopperId : p.shopperId,
    businessUserId: IsEmpty(p.businessUserId) ? defaultPhotoInfo.businessUserId : p.businessUserId,
    commentId:      IsEmpty(p.commentId) ? defaultPhotoInfo.commentId : p.commentId,
    info:           IsEmpty(p.info) ? defaultPhotoInfo.info : p.info,
    isEditable:     isStaffRestricted,
    isDeletable:    isStaffRestricted,
    poiId:          IsEmpty(p.poiId) ? defaultPhotoInfo.poiId : p.poiId,
  };
};

const MARKER_SIZE = 20;

// POI-specific component using the reusable component
const Photos = (props) => {
  const pageRef = useRef(null);
  const [poi, setPoi] = useState(null);
  const [photo, setPhoto] = useState(defaultPhotoInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  const isPoiDeactivated = !IsEmpty(poi) && poi.status === Status.Deactivated;
  const isPhotoNotEditable = IsEmpty(photo) || !photo.isEditable;
  const isDisabled = isPoiDeactivated || isPhotoNotEditable;

  const getPoi = async (id) => {
    try {
      setIsLoading(true);
      const data = await apiService.getPoi(id);
      setPoi({ ...data, photos: _.map(data.photos, formatPhoto) });

      if (IsEmpty(data)) {
        toast.warn('POI not found');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = (name, value) => {
    if (name === 'contributor' && !IsEmpty(value) && !IsEmpty(pageRef.current)) {
      pageRef.current.clear();
    }

    if (name === 'page' && (IsEmpty(value) || IsEmpty(value.id))) {
      value = defaultPhotoInfo.page;
    }

    if (['text', 'contributor', 'takenOn'].includes(name)) {
      setPhoto((prev) => ({ ...prev, info: { ...prev.info, [name]: value } }));
    } else {
      setPhoto((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotoForm = (formData) => {
    IfMapCoordInvalidThrowError(photo.lat, photo.lng);

    const trimmedContributor = Trim(photo.info.contributor);
    const trimmedText = Trim(photo.info.text);

    const hasPage = (!IsEmpty(photo.page) && !IsEmpty(photo.page.id));

    if (hasPage) {
      formData.append('pageId', photo.page.id);
    }

    if (!hasPage && !IsEmpty(trimmedContributor)) {
      formData.append('contributor', trimmedContributor);
    }

    if (!IsEmpty(photo.info.takenOn)) {
      formData.append('takenOn', photo.info.takenOn);
    }

    if (!IsEmpty(trimmedText)) {
      formData.append('text', trimmedText);
    }

    if (!IsEmpty(photo.lat)) {
      formData.append('lat', photo.lat);
    }

    if (!IsEmpty(photo.lng)) {
      formData.append('lng', photo.lng);
    }
  };

  const handleUploadMedia = async (poiId, formData, file) => {
    // We don't use try catch here so the error can be caught by the handleSave method in PhotosComponent
    handlePhotoForm(formData);
    const poiPhoto = await apiService.uploadPoiPhoto(poiId, formData, file);
    return formatPhoto(poiPhoto);
  };

  const handleUpdateMedia = async (poiId, formData, file) => {
    // We don't use try catch here so the error can be caught by the handleSave method in PhotosComponent
    if (!IsEmpty(photo.id)) {
      handlePhotoForm(formData);
      const poiPhoto = await apiService.updatePoiPhoto(poiId, photo.id, formData, file);
      return formatPhoto(poiPhoto);
    }
    return null;
  };

  const handleDeleteMedia = (poiId, ph) => apiService.deletePoiPhoto(poiId, ph.id);

  const onTextFieldChange = ({ target: { name, value } }) => handlePhotoChange(name, value);

  const preventWhitespace = (e) => {
    e.target.value = e.target.value.replace(' ', '');
  };

  useEffect(() => {
    if (!IsEmpty(props.match.params.id)) {
      getPoi(props.match.params.id);
    }
  }, [props]);

  return (
    isLoading ? (
      <MText text="Loading..." />
    ) : (
      !IsEmpty(poi) && (
        <PhotosComponent
          {...props}
          disabled={isPoiDeactivated}
          sortable={false}
          fetchEntity={apiService.getPoi}
          uploadMedia={handleUploadMedia}
          deleteMedia={handleDeleteMedia}
          updateMedia={handleUpdateMedia}
          currentMedia={photo}
          getMediaId={(p) => p.id}
          isUpdating={!IsEmpty(photo.id)}
          isConfirmDisabled={isDisabled}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={800}
          outputPixelHeight={800}
          itemWidth={200}
          itemHeight={200}
          // Note:
          // This method of getting the media is very messy. We should package the entity's media and pass it ot PhotosComponent
          getMediaFromEntity={() => (IsEmpty(poi.photos) ? [] : poi.photos)}
          mediaFieldName="photos"
          title="Preview Image"
          minMediaCount={(poi && poi.status) === Status.Live ? 1 : 0}
          showForm
          onCropperClose={() => setPhoto(defaultPhotoInfo)}
          onSelectImageToCrop={(p) => setPhoto(p)}
          renderForm={() => (
            <form className="w-100">
              {
                !IsEmpty(photo.id) && (
                  <div className="mt-2">
                    <MText
                      size={12}
                      colour={Colours.DarkGray}
                      text={`Photo ID: ${photo.id}`}
                    />
                  </div>
                )
              }

              {!photo.isEditable && (
                <div className="alert alert-primary d-flex justify-content-between align-items-center" role="alert">
                  <MText
                    colour={Colours.MaterialBlue}
                    text={`This photo cannot be edited or deleted because it was uploaded by a ${
                      (!IsEmpty(photo.shopperId) || !IsEmpty(photo.commentId))
                        ? `shopper`
                        : !IsEmpty(photo.businessUserId)
                          ? `business user`
                          : ''
                    }.`}
                  />
                </div>
              )}

              <div className="mt-4">
                <MText text="Photographer Page" />
                <PageSuggestionField
                  ref={pageRef}
                  disabled={isDisabled || (!IsEmpty(photo.info) && !IsEmpty(photo.info.contributor))}
                  value={photo.page}
                  apiParams={{ type: PageType.Photographer }}
                  onChange={(page) => handlePhotoChange('page', page)}
                />
              </div>

              <div className="mt-4">
                <MText text="Contributor Name" />
                <TextField
                  fullWidth
                  name="contributor"
                  placeholder="Contributor"
                  variant="outlined"
                  inputProps={{ style: { fontSize: 13 } }}
                  size="small"
                  disabled={isDisabled || (!IsEmpty(photo.page) && !IsEmpty(photo.page.id))}
                  value={photo.info.contributor}
                  onChange={onTextFieldChange}
                />
              </div>

              <div className="mt-4">
                <MText text="Taken On" />
                <TextField
                  fullWidth
                  name="takenOn"
                  placeholder="Taken On"
                  variant="outlined"
                  type="date"
                  inputProps={{
                    max:   moment().format('YYYY-MM-DD'),
                    style: { fontSize: 13 },
                  }}
                  size="small"
                  disabled={isDisabled}
                  value={photo.info.takenOn}
                  onChange={onTextFieldChange}
                />
              </div>

              <div className="mt-4">
                <MText text="Description" />
                <TextField
                  fullWidth
                  name="text"
                  multiline
                  rows={4}
                  rowsMax={4}
                  placeholder="Description"
                  variant="outlined"
                  inputProps={{ style: { fontSize: 13 } }}
                  size="small"
                  disabled={isDisabled}
                  value={photo.info.text}
                  onChange={onTextFieldChange}
                />
              </div>

              <div className="mt-4">
                <MText text="Location" size={18} bold />
                {
                  !isDisabled && (
                    <MButton
                      text="Pick location from map where the photo was taken"
                      variant="text"
                      className="px-0 pt-0"
                      textColour={Colours.Blue}
                      textStyle={{ fontWeight: 500, textDecoration: 'underline' }}
                      onClick={() => setShowMapModal(true)}
                    />
                  )
                }

                <div className="form-group mt-2">
                  <MText text="Lat" />
                  <input
                    type="text"
                    className="form-control"
                    name="lat"
                    value={photo.lat}
                    onChange={onTextFieldChange}
                    onInput={preventWhitespace}
                    disabled={isDisabled}
                  />
                </div>

                <div className="form-group">
                  <MText text="Lng" />
                  <input
                    type="text"
                    className="form-control"
                    name="lng"
                    value={photo.lng}
                    onChange={onTextFieldChange}
                    onInput={preventWhitespace}
                    disabled={isDisabled}
                  />
                </div>

                <div className="mt-2">
                  <PoiMap
                    disabled
                    isPoiMarker
                    markerIcon={Images.Local.PoiPhotoLocationMarker}
                    markerScaledSize={MARKER_SIZE}
                    mapHeight={250}
                    coord={{ lat: photo.lat, lng: photo.lng }}
                  />
                </div>
              </div>

              <MapModal
                showMapModal={showMapModal}
                closeMapModal={() => setShowMapModal(false)}
                markerIcon={Images.Local.PoiPhotoLocationMarker}
                markerScaledSize={MARKER_SIZE}
                coord={{ lat: photo.lat, lng: photo.lng }}
                onApply={({ lat, lng }) => setPhoto((prev) => ({ ...prev, lat, lng }))}
              />
            </form>
          )}
        />
      )
    )

  );
};

Photos.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default Photos;
